import { useCallback, useContext, useEffect, useState } from 'react';

import ToastContext from 'Context/ToastContext';

import { useToast } from 'Hooks/useToast';

const useQuery = ({ queryFn, successFn, errorFn, enabled = true, params, displayErrorToast = true, errorMessage, successMessage } = {}) => {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const toast = useContext(ToastContext) ? useToast() : null;

    useEffect(() => {
        if (enabled) {
            request(params);
        }
    }, [enabled]);

    /**
     * Makes an API request with the given options
     */
    const request = useCallback(
        async (params) => {
            setLoading(true);
            setError(null);

            try {
                const response = await queryFn(params);

                setData(response.data);

                if (toast && successMessage) {
                    toast.success(successMessage);
                }

                if (successFn && typeof successFn === 'function') {
                    successFn(response.data);
                }

                return Promise.resolve(response);
            } catch (err) {
                setError(err);

                if (toast && displayErrorToast) {
                    toast.error(errorMessage || err.response?.data?.message || err.response?.data?.error || $t('An error occurred, please try again'));
                }

                if (errorFn && typeof errorFn === 'function') {
                    errorFn(err);
                }

                return Promise.reject(err);
            } finally {
                setLoading(false);
            }
        },
        [queryFn, enabled, toast, displayErrorToast, errorMessage, successMessage],
    );

    const reset = useCallback(() => {
        setData(null);
        setLoading(false);
        setError(null);
    }, []);

    return { data, loading, error, refetch: request, reset };
};

export default useQuery;
